import AsyncStorage from '@react-native-async-storage/async-storage';
import { generateId } from '../utils/id';
import type { Landmark, PresetRun, Run, RunSegment, SegmentStats, CommunitySegmentStats } from '../types';

// ── Storage keys ──────────────────────────────────────────────────────────────
const KEYS = {
  landmarks: '@lmrun/landmarks',
  presetRuns: '@lmrun/preset-runs',
  runs: '@lmrun/runs',
} as const;

// ── Helpers ───────────────────────────────────────────────────────────────────
async function readList<T>(key: string): Promise<T[]> {
  const raw = await AsyncStorage.getItem(key);
  return raw ? (JSON.parse(raw) as T[]) : [];
}

async function writeList<T>(key: string, list: T[]): Promise<void> {
  await AsyncStorage.setItem(key, JSON.stringify(list));
}

// ── Landmarks ─────────────────────────────────────────────────────────────────
export const landmarksApi = {
  list: () => readList<Landmark>(KEYS.landmarks),

  listPublic: (_lat: number, _lng: number, _radiusKm = 50) =>
    readList<Landmark>(KEYS.landmarks),

  create: async (body: Omit<Landmark, 'landmarkId' | 'userId' | 'createdAt'>): Promise<Landmark> => {
    const list = await readList<Landmark>(KEYS.landmarks);
    const item: Landmark = {
      ...body,
      landmarkId: generateId(),
      userId: 'local',
      createdAt: new Date().toISOString(),
    };
    await writeList(KEYS.landmarks, [...list, item]);
    return item;
  },

  update: async (id: string, body: Partial<Landmark>): Promise<Landmark> => {
    const list = await readList<Landmark>(KEYS.landmarks);
    const idx = list.findIndex((l) => l.landmarkId === id);
    if (idx < 0) throw new Error(`Landmark ${id} not found`);
    list[idx] = { ...list[idx], ...body };
    await writeList(KEYS.landmarks, list);
    return list[idx];
  },

  delete: async (id: string): Promise<void> => {
    const list = await readList<Landmark>(KEYS.landmarks);
    await writeList(KEYS.landmarks, list.filter((l) => l.landmarkId !== id));
  },
};

// ── Preset Runs ───────────────────────────────────────────────────────────────
export const presetRunsApi = {
  list: () => readList<PresetRun>(KEYS.presetRuns),

  listPublic: () => readList<PresetRun>(KEYS.presetRuns),

  create: async (body: Omit<PresetRun, 'presetRunId' | 'userId' | 'createdAt'>): Promise<PresetRun> => {
    const list = await readList<PresetRun>(KEYS.presetRuns);
    const item: PresetRun = {
      ...body,
      presetRunId: generateId(),
      userId: 'local',
      createdAt: new Date().toISOString(),
    };
    await writeList(KEYS.presetRuns, [...list, item]);
    return item;
  },

  delete: async (id: string): Promise<void> => {
    const list = await readList<PresetRun>(KEYS.presetRuns);
    await writeList(KEYS.presetRuns, list.filter((r) => r.presetRunId !== id));
  },
};

// ── Runs ──────────────────────────────────────────────────────────────────────
export const runsApi = {
  list: () => readList<Run>(KEYS.runs),

  get: async (id: string): Promise<Run> => {
    const list = await readList<Run>(KEYS.runs);
    const run = list.find((r) => r.runId === id);
    if (!run) throw new Error(`Run ${id} not found`);
    return run;
  },

  start: async (body: { mode: 'preset' | 'free'; presetRunId?: string }): Promise<{ runId: string }> => {
    const runId = generateId();
    const list = await readList<Run>(KEYS.runs);
    const newRun: Run = {
      runId,
      userId: 'local',
      mode: body.mode,
      presetRunId: body.presetRunId,
      startedAt: new Date().toISOString(),
      totalDistanceMiles: 0,
      totalDurationSecs: 0,
      segments: [],
    };
    await writeList(KEYS.runs, [...list, newRun]);
    return { runId };
  },

  addSegment: async (runId: string, segment: RunSegment): Promise<void> => {
    const list = await readList<Run>(KEYS.runs);
    const idx = list.findIndex((r) => r.runId === runId);
    if (idx >= 0) {
      list[idx] = { ...list[idx], segments: [...(list[idx].segments ?? []), segment] };
      await writeList(KEYS.runs, list);
    }
  },

  finish: async (
    runId: string,
    body: { completedAt: string; totalDistanceMiles: number; totalDurationSecs: number }
  ): Promise<Run> => {
    const list = await readList<Run>(KEYS.runs);
    const idx = list.findIndex((r) => r.runId === runId);
    if (idx < 0) throw new Error(`Run ${runId} not found`);
    list[idx] = { ...list[idx], ...body };
    await writeList(KEYS.runs, list);
    return list[idx];
  },
};

// ── Stats (computed locally from run history) ─────────────────────────────────
export const statsApi = {
  personal: async (segmentKey: string): Promise<SegmentStats> => {
    const runs = await readList<Run>(KEYS.runs);
    const times = runs
      .flatMap((r) => r.segments ?? [])
      .filter((s) => `${s.fromLandmarkId}-${s.toLandmarkId}` === segmentKey)
      .map((s) => s.durationSecs);

    if (times.length === 0) {
      return { count: 0, segmentKey, bestSecs: 0, avgSecs: 0, stdDevSecs: 0, latestSecs: 0, deltaFromBestSecs: 0, deltaFromAvgSecs: 0, deviationsFromMean: 0 };
    }
    const best = Math.min(...times);
    const avg = times.reduce((a, b) => a + b, 0) / times.length;
    const variance = times.reduce((a, b) => a + Math.pow(b - avg, 2), 0) / times.length;
    const stdDev = Math.sqrt(variance);
    const latest = times[times.length - 1];
    return {
      segmentKey,
      count: times.length,
      bestSecs: best,
      avgSecs: avg,
      stdDevSecs: stdDev,
      latestSecs: latest,
      deltaFromBestSecs: latest - best,
      deltaFromAvgSecs: latest - avg,
      deviationsFromMean: stdDev > 0 ? (latest - avg) / stdDev : 0,
    };
  },

  // Community stats require a server — return empty gracefully
  community: async (segmentKey: string, groupType: 'age' | 'gender' | 'named', groupValue: string): Promise<CommunitySegmentStats> => ({
    segmentKey,
    groupType,
    groupValue,
    participantCount: 0,
    avgSecs: 0,
    stdDevSecs: 0,
  }),
};
